import { BrowserAgent, Ajax, JSErrors, Metrics, PageAction, PageViewEvent, PageViewTiming, SessionTrace, Spa } from '@newrelic/browser-agent';

const getNewRelicConfig = (templateConfig: any): any => {
  const variables = templateConfig?.pages?.features?.newRelic || {};
  const shouldApplyMasking = !variables.unmaskSensitiveFields;

  const options = {
    init: {
      session_replay: {
        enabled: true,
        block_selector: 'nr-block',
        mask_text_selector: 'nr-mask',
        sampling_rate: 100.0,
        error_sampling_rate: 100.0,
        mask_all_inputs: false,
        collect_fonts: true,
        inline_images: false,
        inline_stylesheet: true,
        fix_stylesheets: true,
        preload: true,
        mask_input_options: shouldApplyMasking ? {} : { password: false },
      },
      distributed_tracing: {
        enabled: true,
      },
      privacy: {
        cookies_enabled: true,
        maskAllInputs: false,
      },
      ajax: {
        deny_list: ['bam.nr-data.net'],
        enabled: true,
        harvestTimeSeconds: 10,
      },
    },
    loader_config: {
      accountID: String(variables.accountID || variables.trustKey),
      trustKey: String(variables.trustKey),
      agentID: String(variables.agentID),
      licenseKey: String(variables.licenseKey),
      applicationID: String(variables.applicationID),
    },
    info: {
      beacon: String(variables.beacon),
      errorBeacon: String(variables.errorBeacon),
      licenseKey: String(variables.licenseKey),
      applicationID: String(variables.applicationID),
      sa: String(variables.sa),
    },
    features: [Ajax, JSErrors, Metrics, PageAction, PageViewEvent, PageViewTiming, SessionTrace, Spa],
  };
  return options;
};

let newRelicAgent: BrowserAgent | null = null;

export const initializeNewRelic = (templateConfig: any, userEmail?: string) => {
  if (!newRelicAgent) {
    try {
      if (typeof window !== 'undefined' && (window as any).newrelic) {
        const windowNewrelic = (window as any).newrelic;
        const hasSetCurrentRouteName = typeof windowNewrelic.setCurrentRouteName === 'function';

        if (hasSetCurrentRouteName) {
          return;
        }
      }

      const newRelicConfig = getNewRelicConfig(templateConfig);

      if (!newRelicConfig.info?.applicationID || !newRelicConfig.info?.licenseKey) {
        return;
      }

      newRelicAgent = new BrowserAgent(newRelicConfig);

      if (userEmail) {
        newRelicAgent.setUserId(userEmail);
      }

      if (typeof window !== 'undefined') {
        const initialPath = window.location.pathname;
        try {
          newRelicAgent.setCurrentRouteName(initialPath);
        } catch (error) {
          // Ignore errors
        }
      }
    } catch (error) {
      // Ignore errors
    }
  }
};

export const updateNewRelicUser = (userEmail: string) => {
  if (newRelicAgent) {
    try {
      newRelicAgent.setUserId(userEmail);
    } catch (error) {
      // Error updating user ID
    }
  }
};

export const setCustomNewRelicAttribute = (customNewRelicAttribute: string, customNewRelicAttributeValue: string) => {
  if (newRelicAgent && customNewRelicAttribute != null) {
    try {
      newRelicAgent.setCustomAttribute(customNewRelicAttribute, customNewRelicAttributeValue);
    } catch (error) {
      // Error updating custom attribute
    }
  }
};

export const trackPageView = (routeName: string) => {
  if (newRelicAgent) {
    try {
      newRelicAgent.setCurrentRouteName(routeName);
    } catch (error) {
      console.error(error);
    }
  } else if (typeof window !== 'undefined' && (window as any).newrelic) {
    const windowNewrelic = (window as any).newrelic;
    if (typeof windowNewrelic.setCurrentRouteName === 'function') {
      try {
        windowNewrelic.setCurrentRouteName(routeName);
      } catch (error) {
        console.error(error);
      }
    }
  }
};

const debugNewRelic = () => {
  let beaconCount = 0;
  let ajaxCount = 0;

  if (typeof window !== 'undefined' && 'performance' in window && 'getEntriesByType' in window.performance) {
    const resourceEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    beaconCount = resourceEntries.filter((entry) => entry.name.includes('bam.nr-data.net') || entry.name.includes('nr-data.net')).length;

    ajaxCount = resourceEntries.filter((entry) => entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch').length;
  }

  const debugInfo = {
    agentInitialized: !!newRelicAgent,
    agentType: newRelicAgent ? typeof newRelicAgent : 'null',
    agentMethods: newRelicAgent ? Object.getOwnPropertyNames(Object.getPrototypeOf(newRelicAgent)) : [],
    windowNewRelic: typeof window !== 'undefined' ? (window as any).newrelic : 'N/A',
    beaconCount,
    ajaxRequestCount: ajaxCount,
    currentRoute: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
    applicationID: newRelicAgent ? (newRelicAgent as any).info?.applicationID : 'N/A',
  };

  return debugInfo;
};

if (typeof window !== 'undefined') {
  (window as any).debugNewRelic = debugNewRelic;
}

export const logConsent = (consentValue: boolean, userEmail?: string) => {
  const consentData: any = {
    consentValue,
    consentTimestamp: new Date().toISOString(),
  };

  if (userEmail) {
    consentData.borrowerName = userEmail;
  }

  updateNewRelicMetadata(consentData);
  trackCustomEvent('consent_given', consentData);
};

const updateNewRelicMetadata = (metadata: any) => {
  if (!newRelicAgent) {
    return;
  }

  try {
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined) {
        newRelicAgent!.setCustomAttribute(key, value as string | number | boolean | null);
      }
    });
  } catch (error) {
    console.error(error);
  }
};

const trackCustomEvent = (eventName: string, attributes: Record<string, unknown>) => {
  if (newRelicAgent) {
    try {
      newRelicAgent.noticeError(new Error(eventName), attributes);
    } catch (error) {
      console.error(error);
    }
  }
};
